import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { CalendarClock } from 'lucide-react';
import CashierLayout from '../components/CashierLayout';
import { cashierListAssignedShifts, ensureCashierDeviceRegistered, CashierShift } from '../services/cashierApi';
import { getStoredDeviceId } from '../utils/deviceFingerprint';
import { canJoinShift, canStartShift, isShiftViewable, shiftNotViewableMessage } from '../utils/shiftAccess';

function formatShiftWindow(shift: CashierShift): string {
  if (shift.scheduledStartAt && shift.scheduledEndAt) {
    const start = new Date(shift.scheduledStartAt).toLocaleString();
    const end = new Date(shift.scheduledEndAt).toLocaleTimeString();
    return `${start} – ${end}`;
  }
  if (shift.shiftDate) return new Date(shift.shiftDate).toLocaleDateString();
  return '—';
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'active':
      return 'bg-emerald-50 text-emerald-700';
    case 'scheduled':
      return 'bg-blue-50 text-blue-700';
    case 'auto_locked':
      return 'bg-amber-50 text-amber-700';
    case 'no_show':
      return 'bg-orange-50 text-orange-700';
    case 'cancelled':
      return 'bg-stone-100 text-stone-500';
    case 'pending_reconciliation':
    case 'pending_manager_review':
      return 'bg-rose-50 text-rose-700';
    default:
      return 'bg-stone-100 text-stone-700';
  }
}

function getShiftAction(shift: CashierShift): { path: string; label: string } | null {
  if (!isShiftViewable(shift.status)) return null;
  if (canStartShift(shift.status)) return { path: `/shifts/${shift.id}/opening`, label: 'Start shift' };
  if (canJoinShift(shift.status)) return { path: `/shifts/${shift.id}`, label: 'Open shift' };
  return { path: `/shifts/${shift.id}`, label: 'View shift' };
}

export default function MyShiftsPage() {
  const [shifts, setShifts] = useState<CashierShift[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await cashierListAssignedShifts();
      if (!res.success) {
        toast.error(res.message || 'Failed to load shifts');
        setShifts([]);
        return;
      }
      setShifts(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (!getStoredDeviceId()) {
        const reg = await ensureCashierDeviceRegistered();
        if (reg.ok) {
          toast.success('This device was registered. Shop manager must approve it under Devices.');
        }
      }
      await load();
    };
    void init();
  }, []);

  return (
    <CashierLayout title="My shifts" subtitle="All shifts for your account">
      <div className="rounded-xl border border-stone-200 bg-white p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-semibold text-stone-900">All shifts</div>
          <button
            type="button"
            onClick={() => void load()}
            className="rounded-lg border border-stone-200 px-3 py-1.5 text-sm font-semibold hover:bg-stone-50"
          >
            Refresh
          </button>
        </div>

        <div className="mt-4">
          {loading ? (
            <div className="text-sm text-stone-600">Loading…</div>
          ) : shifts.length === 0 ? (
            <div className="rounded-lg border border-dashed border-stone-200 p-6 text-center text-sm text-stone-600">
              No shifts right now.
            </div>
          ) : (
            <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
            {shifts.map((shift) => (
              <div key={shift.id} className="rounded-lg border border-stone-200 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <CalendarClock className="h-4 w-4 text-stone-500" />
                      <span className="text-sm font-semibold text-stone-900">{formatShiftWindow(shift)}</span>
                    </div>
                    <div className="mt-1 text-xs text-stone-500">ID: {shift.id}</div>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase ${statusBadgeClass(shift.status)}`}>
                    {shift.status.replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="mt-3">
                  {(() => {
                    const action = getShiftAction(shift);
                    if (!action) {
                      return (
                        <p className="text-sm text-stone-600">{shiftNotViewableMessage(shift.status)}</p>
                      );
                    }
                    return (
                      <Link
                        to={action.path}
                        className="inline-flex rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                      >
                        {action.label}
                      </Link>
                    );
                  })()}
                </div>
              </div>
            ))}
            </div>
          )}
        </div>
      </div>
    </CashierLayout>
  );
}
